import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useNavigate } from 'react-router-dom';
import DictationInput from '../../components/DictationInput';
import { toast } from 'sonner';
import {
  Flame, Trophy, Users, CalendarDays, BookOpen, MessageSquare,
  FileText, ChevronDown, ChevronRight, User, Send, Clock,
  ClipboardList, ExternalLink
} from 'lucide-react';

export default function HeatDashboardPage() {
  const { user, axiosAuth } = useAuth();
  const api = axiosAuth();
  const navigate = useNavigate();

  const [modules, setModules] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [facilitators, setFacilitators] = useState([]);
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportMessage, setSupportMessage] = useState('');
  const [clients, setClients] = useState([]);
  const [specialists, setSpecialists] = useState([]);
  const [referralSources, setReferralSources] = useState([]);
  const [enrollForm, setEnrollForm] = useState({ client_id: '', assigned_specialist_id: '', referred_by_referral_source_id: '' });

  // Documentation state
  const [activeTab, setActiveTab] = useState('overview');
  const [groupDocs, setGroupDocs] = useState([]);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [groupMessageOpen, setGroupMessageOpen] = useState(null);
  const [groupMessageText, setGroupMessageText] = useState('');
  const [participants, setParticipants] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDocs, setClientDocs] = useState(null);
  const [loadingClientDocs, setLoadingClientDocs] = useState(false);
  const [expandedNote, setExpandedNote] = useState(null);

  const canEnroll = ['admin', 'coordinator'].includes(user.role);
  const isFacilitator = user.role === 'facilitator';
  const isPeer = user.role === 'peer_specialist';
  const isCoordinator = user.role === 'coordinator';
  const isExecutive = user.role === 'admin';
  const isParticipant = user.role === 'client';
  const isStaff = isPeer || isFacilitator || isCoordinator || isExecutive;

  useEffect(() => {
    api.get('/heat/modules').then(r => setModules(r.data)).catch(() => {});
    api.get('/heat/completions').then(r => setCompletions(r.data)).catch(() => {});
    api.get('/heat/sessions').then(r => setSessions(r.data)).catch(() => {});
    api.get('/heat/facilitators').then(r => setFacilitators(r.data)).catch(() => {});
    if (isStaff) {
      api.get('/heat/participants').then(r => setParticipants(r.data)).catch(() => {});
      api.get('/heat/documentation/group').then(r => setGroupDocs(r.data)).catch(() => {});
    }
    if (canEnroll) {
      api.get('/users?role=client').then(r => setClients(r.data)).catch(() => {});
      api.get('/users?role=peer_specialist').then(r => setSpecialists(r.data)).catch(() => {});
      api.get('/users?role=referral_source').then(r => setReferralSources(r.data)).catch(() => {});
    }
  }, []);

  const completedCount = completions.length;
  const totalModules = modules.length;
  const progress = totalModules ? Math.round((completedCount / totalModules) * 100) : 0;
  const pendingApprovals = completions.filter(c => c.approval_status && c.approval_status.startsWith('pending'));

  const handleSupportRequest = async () => {
    if (!supportMessage.trim()) return toast.error('Please share your question');
    try {
      await api.post('/heat/escalate', { content: supportMessage.trim() });
      toast.success('Request sent to coordinator');
      setSupportMessage('');
      setSupportOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to send request');
    }
  };

  const handleEnroll = async () => {
    if (!enrollForm.client_id) return toast.error('Select a participant');
    try {
      await api.post('/heat/enroll', {
        client_id: enrollForm.client_id,
        assigned_specialist_id: enrollForm.assigned_specialist_id || undefined,
        referred_by_referral_source_id: enrollForm.referred_by_referral_source_id || undefined
      });
      toast.success('Participant enrolled in HEAT');
      setEnrollForm({ client_id: '', assigned_specialist_id: '', referred_by_referral_source_id: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to enroll participant');
    }
  };

  const handleSendGroupMessage = async (noteId) => {
    if (!groupMessageText.trim()) return toast.error('Please enter a message');
    try {
      const res = await api.post(`/heat/documentation/group/${noteId}/message`, { content: groupMessageText.trim() });
      toast.success(`Message sent to ${res.data.sent_to?.length || 0} leader(s)`);
      setGroupMessageText('');
      setGroupMessageOpen(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
    }
  };

  const loadClientDocs = async (clientId) => {
    setSelectedClient(clientId);
    setLoadingClientDocs(true);
    try {
      const res = await api.get(`/heat/documentation/individual/${clientId}`);
      setClientDocs(res.data);
    } catch (err) {
      toast.error('Failed to load client documentation');
      setClientDocs(null);
    } finally {
      setLoadingClientDocs(false);
    }
  };

  const tabs = [
    { key: 'overview', label: 'Overview', icon: Flame },
    ...(isStaff ? [{ key: 'group_docs', label: 'Group Documentation', icon: FileText }] : []),
    ...(isStaff ? [{ key: 'individual_docs', label: 'Individual Documentation', icon: User }] : []),
  ];

  return (
    <div className="space-y-6" data-testid="heat-dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>
            {isFacilitator ? 'HEAT Facilitator Hub' : isPeer ? 'HEAT Peer Support Hub' : 'HEAT Program Hub'}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {isFacilitator
              ? 'Deliver curriculum, track attendance, and document module progress.'
              : isPeer
                ? 'Support participants, review module submissions, and coordinate with facilitators.'
                : 'HEAT curriculum milestones, attendance tracking, and facilitator guidance.'}
          </p>
        </div>
        <Button onClick={() => navigate('/heat/modules')} className="bg-orange-600 hover:bg-orange-700 text-white rounded-full gap-2" data-testid="heat-go-modules">
          <Flame size={16} /> View Modules
        </Button>
      </div>

      {/* Tab Navigation */}
      {isStaff && (
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1" data-testid="heat-hub-tabs">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => { setActiveTab(tab.key); setSelectedClient(null); setClientDocs(null); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.key
                    ? 'bg-white text-orange-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid={`heat-tab-${tab.key}`}
              >
                <Icon size={15} />
                {tab.label}
              </button>
            );
          })}
        </div>
      )}

      {/* ============ OVERVIEW TAB ============ */}
      {activeTab === 'overview' && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="border-0 shadow-sm rounded-2xl cursor-pointer" data-testid="heat-stat-modules" onClick={() => navigate('/heat/modules')}>
              <CardContent className="p-4">
                <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center mb-2"><BookOpen className="w-4 h-4 text-orange-600" /></div>
                <p className="text-2xl font-bold">{totalModules}</p>
                <p className="text-xs text-muted-foreground">HEAT Modules</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm rounded-2xl cursor-pointer" data-testid="heat-stat-completions" onClick={() => navigate('/heat/modules')}>
              <CardContent className="p-4">
                <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center mb-2"><Trophy className="w-4 h-4 text-emerald-600" /></div>
                <p className="text-2xl font-bold">{completedCount}</p>
                <p className="text-xs text-muted-foreground">Completions</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm rounded-2xl cursor-pointer" data-testid="heat-stat-sessions" onClick={() => navigate('/heat/attendance')}>
              <CardContent className="p-4">
                <div className="w-9 h-9 rounded-lg bg-sky-50 flex items-center justify-center mb-2"><CalendarDays className="w-4 h-4 text-sky-600" /></div>
                <p className="text-2xl font-bold">{sessions.length}</p>
                <p className="text-xs text-muted-foreground">Attendance Sessions</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm rounded-2xl cursor-pointer" data-testid="heat-stat-facilitators" onClick={() => navigate('/heat/facilitators')}>
              <CardContent className="p-4">
                <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center mb-2"><Users className="w-4 h-4 text-indigo-600" /></div>
                <p className="text-2xl font-bold">{facilitators.length}</p>
                <p className="text-xs text-muted-foreground">Facilitators</p>
              </CardContent>
            </Card>
          </div>

          {isStaff && (
            <Card className="border-0 shadow-sm rounded-2xl" data-testid="heat-approvals-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold">Pending Module Approvals</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {pendingApprovals.slice(0, 4).map(item => (
                  <div key={item.id} className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid={`heat-approval-${item.id}`}>
                    <span>{item.module_name}</span>
                    <Badge variant="secondary" className="bg-amber-50 text-amber-700">Pending</Badge>
                  </div>
                ))}
                {pendingApprovals.length === 0 && (
                  <p className="text-sm text-muted-foreground">No approvals waiting.</p>
                )}
                <Button variant="outline" className="rounded-full" onClick={() => navigate('/heat/modules')} data-testid="heat-approval-view">
                  Review Modules
                </Button>
              </CardContent>
            </Card>
          )}

          {canEnroll && (
            <Card className="border-0 shadow-sm rounded-2xl" data-testid="heat-enroll-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold">Enroll Participant in HEAT</CardTitle>
              </CardHeader>
              <CardContent className="grid md:grid-cols-3 gap-3">
                <Select value={enrollForm.client_id} onValueChange={value => setEnrollForm(prev => ({ ...prev, client_id: value }))}>
                  <SelectTrigger data-testid="heat-enroll-client"><SelectValue placeholder="Participant" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={enrollForm.assigned_specialist_id} onValueChange={value => setEnrollForm(prev => ({ ...prev, assigned_specialist_id: value }))}>
                  <SelectTrigger data-testid="heat-enroll-specialist"><SelectValue placeholder="Assign Specialist (optional)" /></SelectTrigger>
                  <SelectContent>
                    {specialists.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={enrollForm.referred_by_referral_source_id} onValueChange={value => setEnrollForm(prev => ({ ...prev, referred_by_referral_source_id: value }))}>
                  <SelectTrigger data-testid="heat-enroll-referral"><SelectValue placeholder="Referral Source (optional)" /></SelectTrigger>
                  <SelectContent>
                    {referralSources.map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-full md:col-span-3" onClick={handleEnroll} data-testid="heat-enroll-submit">
                  Enroll Participant
                </Button>
              </CardContent>
            </Card>
          )}

          <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-orange-50 to-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Flame className="w-4 h-4 text-orange-600" /> HEAT Progress Snapshot
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-orange-900">
                {totalModules === 0
                  ? 'Modules are loading. Your HEAT milestones will appear here once the program starts.'
                  : `You have completed ${completedCount} of ${totalModules} modules (${progress}%). Keep building consistency.`}
              </p>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-2 gap-4">
            <Card className="border-0 shadow-sm rounded-2xl" data-testid="heat-quick-links">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold">HEAT Actions</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-2">
                <Button variant="outline" className="h-auto py-3 rounded-xl" onClick={() => navigate('/heat/modules')} data-testid="heat-link-modules">
                  Modules
                </Button>
                <Button variant="outline" className="h-auto py-3 rounded-xl" onClick={() => navigate('/heat/attendance')} data-testid="heat-link-attendance">
                  Attendance
                </Button>
                {isStaff && (
                  <Button variant="outline" className="h-auto py-3 rounded-xl" onClick={() => navigate('/heat/messages')} data-testid="heat-link-messages">
                    HEAT Messages
                  </Button>
                )}
                {(isPeer || isCoordinator || isExecutive) && (
                  <Button variant="outline" className="h-auto py-3 rounded-xl" onClick={() => navigate('/heat/facilitators')} data-testid="heat-link-facilitators">
                    Facilitators
                  </Button>
                )}
                {user.role === 'referral_source' && (
                  <Button variant="outline" className="h-auto py-3 rounded-xl" onClick={() => navigate('/heat/referral-portal')} data-testid="heat-link-referral">
                    Referral Portal
                  </Button>
                )}
              </CardContent>
            </Card>

            {user.role === 'client' && (
              <Card className="border-0 shadow-sm rounded-2xl" data-testid="heat-support-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-teal-600" /> Request Coordinator Support
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">Use this when you need program-level guidance beyond your peer specialist.</p>
                  <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full" onClick={() => setSupportOpen(true)} data-testid="heat-support-open">
                    Request Support
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}

      {/* ============ GROUP DOCUMENTATION TAB ============ */}
      {activeTab === 'group_docs' && isStaff && (
        <div className="space-y-4" data-testid="heat-group-docs-section">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <FileText size={18} className="text-orange-600" /> Group Documentation
            </h2>
            <Badge variant="secondary" className="bg-orange-50 text-orange-700">{groupDocs.length} Records</Badge>
          </div>

          {groupDocs.length === 0 ? (
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-8 text-center">
                <FileText size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-muted-foreground text-sm">No HEAT group documentation found yet.</p>
                <p className="text-muted-foreground text-xs mt-1">Group notes with HEAT, HEAT for Her, or HEAT for Youth types will appear here.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {groupDocs.map(doc => {
                const isExpanded = expandedGroup === doc.id;
                return (
                  <Card key={doc.id} className="border-0 shadow-sm rounded-2xl overflow-hidden" data-testid={`group-doc-${doc.id}`}>
                    <div
                      className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => setExpandedGroup(isExpanded ? null : doc.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center">
                          <FileText className="w-4 h-4 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{doc.group_topic || 'Untitled Group'}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <Badge variant="outline" className="text-[10px] py-0">{doc.group_type}</Badge>
                            <span className="text-xs text-muted-foreground">{doc.date}</span>
                            <span className="text-xs text-muted-foreground">by {doc.author_name}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className={
                          doc.approval_status === 'approved' ? 'bg-emerald-50 text-emerald-700' :
                          doc.approval_status === 'rejected' ? 'bg-red-50 text-red-700' :
                          'bg-amber-50 text-amber-700'
                        }>
                          {doc.approval_status || 'pending'}
                        </Badge>
                        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-3">
                        <div className="grid md:grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Participant Engagement</p>
                            <p className="text-sm bg-slate-50 rounded-lg p-2">{doc.participant_engagement || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Group Outcomes</p>
                            <p className="text-sm bg-slate-50 rounded-lg p-2">{doc.group_outcomes || 'N/A'}</p>
                          </div>
                        </div>
                        {doc.next_group_plan && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Next Group Plan</p>
                            <p className="text-sm bg-slate-50 rounded-lg p-2">{doc.next_group_plan}</p>
                          </div>
                        )}
                        {doc.additional_notes && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Additional Notes</p>
                            <p className="text-sm bg-slate-50 rounded-lg p-2">{doc.additional_notes}</p>
                          </div>
                        )}

                        <div className="flex gap-2 pt-2 border-t border-slate-100">
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full gap-1.5 text-orange-700 border-orange-200 hover:bg-orange-50"
                            onClick={(e) => { e.stopPropagation(); setGroupMessageOpen(doc.id); setGroupMessageText(''); }}
                            data-testid={`group-doc-message-btn-${doc.id}`}
                          >
                            <Send size={13} /> Message Coordinator & ED
                          </Button>
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ============ INDIVIDUAL DOCUMENTATION TAB ============ */}
      {activeTab === 'individual_docs' && isStaff && (
        <div className="space-y-4" data-testid="heat-individual-docs-section">
          {!selectedClient ? (
            <>
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <User size={18} className="text-orange-600" /> Individual Documentation
                </h2>
                <Badge variant="secondary" className="bg-orange-50 text-orange-700">{participants.length} Participants</Badge>
              </div>

              {participants.length === 0 ? (
                <Card className="border-0 shadow-sm rounded-2xl">
                  <CardContent className="p-8 text-center">
                    <Users size={40} className="mx-auto text-slate-300 mb-3" />
                    <p className="text-muted-foreground text-sm">No HEAT participants found.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {participants.map(p => (
                    <Card
                      key={p.id}
                      className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all"
                      onClick={() => loadClientDocs(p.id)}
                      data-testid={`heat-participant-${p.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-amber-400 flex items-center justify-center text-white font-bold text-sm">
                              {(p.full_name || '?')[0].toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-sm">{p.full_name || 'Unknown'}</p>
                              <p className="text-xs text-muted-foreground capitalize">{(p.role || '').replace('_', ' ')}</p>
                            </div>
                          </div>
                          <ChevronRight size={16} className="text-muted-foreground mt-1" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="rounded-full gap-1 text-orange-700"
                onClick={() => { setSelectedClient(null); setClientDocs(null); setExpandedNote(null); }}
                data-testid="heat-individual-back"
              >
                <ChevronRight size={14} className="rotate-180" /> Back to Participants
              </Button>

              {loadingClientDocs ? (
                <Card className="border-0 shadow-sm rounded-2xl">
                  <CardContent className="p-8 text-center">
                    <div className="w-8 h-8 border-3 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">Loading documentation...</p>
                  </CardContent>
                </Card>
              ) : clientDocs ? (
                <div className="space-y-4">
                  {/* Client Header */}
                  <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-orange-50 to-amber-50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white font-bold text-lg">
                          {(clientDocs.client?.full_name || '?')[0].toUpperCase()}
                        </div>
                        <div>
                          <h3 className="font-bold text-base">{clientDocs.client?.full_name}</h3>
                          <div className="flex items-center gap-2 mt-0.5">
                            {clientDocs.client?.programs?.map(p => (
                              <Badge key={p} variant="outline" className="text-[10px] py-0">{p}</Badge>
                            ))}
                            {clientDocs.client?.in_peer_support && (
                              <Badge className="bg-teal-50 text-teal-700 text-[10px] py-0 border-0">
                                <ExternalLink size={9} className="mr-0.5" /> Linked to Peer Support
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* HEAT Module Completions */}
                  <Card className="border-0 shadow-sm rounded-2xl" data-testid="heat-individual-completions">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        <Trophy size={15} className="text-emerald-600" /> HEAT Module Completions ({clientDocs.heat_completions?.length || 0})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {(clientDocs.heat_completions || []).length === 0 ? (
                        <p className="text-sm text-muted-foreground">No module completions yet.</p>
                      ) : (
                        clientDocs.heat_completions.slice(0, 10).map(comp => (
                          <div key={comp.id} className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2 text-sm">
                            <div>
                              <p className="font-medium text-xs">{comp.module_name}</p>
                              <p className="text-[10px] text-muted-foreground">{comp.completed_at?.split('T')[0]}</p>
                            </div>
                            <Badge variant="secondary" className={
                              comp.approval_status === 'approved' ? 'bg-emerald-50 text-emerald-700' :
                              comp.approval_status === 'rejected' ? 'bg-red-50 text-red-700' :
                              'bg-amber-50 text-amber-700'
                            }>
                              {comp.approval_status}
                            </Badge>
                          </div>
                        ))
                      )}
                    </CardContent>
                  </Card>

                  {/* Session Notes */}
                  <Card className="border-0 shadow-sm rounded-2xl" data-testid="heat-individual-session-notes">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        <ClipboardList size={15} className="text-sky-600" /> Session Notes ({clientDocs.session_notes?.length || 0})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {(clientDocs.session_notes || []).length === 0 ? (
                        <p className="text-sm text-muted-foreground">No session notes for this client.</p>
                      ) : (
                        clientDocs.session_notes.slice(0, 10).map(note => {
                          const isOpen = expandedNote === `sn-${note.id}`;
                          return (
                            <div key={note.id} className="rounded-xl border border-slate-200 overflow-hidden" data-testid={`session-note-${note.id}`}>
                              <div
                                className="flex items-center justify-between px-3 py-2 text-sm cursor-pointer hover:bg-slate-50"
                                onClick={() => setExpandedNote(isOpen ? null : `sn-${note.id}`)}
                              >
                                <div className="flex items-center gap-2">
                                  <Clock size={13} className="text-muted-foreground" />
                                  <span className="font-medium text-xs">{note.session_type || 'Session'} - {note.created_at?.split('T')[0]}</span>
                                  <span className="text-[10px] text-muted-foreground">by {note.specialist_name}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="secondary" className={
                                    note.approval_status === 'approved' ? 'bg-emerald-50 text-emerald-700 text-[10px]' :
                                    'bg-amber-50 text-amber-700 text-[10px]'
                                  }>{note.approval_status}</Badge>
                                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                </div>
                              </div>
                              {isOpen && (
                                <div className="px-3 pb-3 border-t border-slate-100 pt-2 space-y-2 text-xs">
                                  {note.subjective && <div><strong className="text-muted-foreground">Subjective:</strong> <span>{note.subjective}</span></div>}
                                  {note.objective && <div><strong className="text-muted-foreground">Objective:</strong> <span>{note.objective}</span></div>}
                                  {note.assessment && <div><strong className="text-muted-foreground">Assessment:</strong> <span>{note.assessment}</span></div>}
                                  {note.plan && <div><strong className="text-muted-foreground">Plan:</strong> <span>{note.plan}</span></div>}
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </CardContent>
                  </Card>

                  {/* Peer Docs */}
                  <Card className="border-0 shadow-sm rounded-2xl" data-testid="heat-individual-peer-docs">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        <FileText size={15} className="text-violet-600" /> Peer Documentation ({clientDocs.peer_docs?.length || 0})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {(clientDocs.peer_docs || []).length === 0 ? (
                        <p className="text-sm text-muted-foreground">No peer documentation for this client.</p>
                      ) : (
                        clientDocs.peer_docs.slice(0, 10).map(doc => {
                          const isOpen = expandedNote === `pd-${doc.id}`;
                          return (
                            <div key={doc.id} className="rounded-xl border border-slate-200 overflow-hidden" data-testid={`peer-doc-${doc.id}`}>
                              <div
                                className="flex items-center justify-between px-3 py-2 text-sm cursor-pointer hover:bg-slate-50"
                                onClick={() => setExpandedNote(isOpen ? null : `pd-${doc.id}`)}
                              >
                                <div className="flex items-center gap-2">
                                  <FileText size={13} className="text-muted-foreground" />
                                  <span className="font-medium text-xs">{doc.interaction_type || 'Peer Doc'} - {doc.created_at?.split('T')[0]}</span>
                                  <span className="text-[10px] text-muted-foreground">by {doc.specialist_name}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="secondary" className={
                                    doc.approval_status === 'approved' ? 'bg-emerald-50 text-emerald-700 text-[10px]' :
                                    'bg-amber-50 text-amber-700 text-[10px]'
                                  }>{doc.approval_status}</Badge>
                                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                </div>
                              </div>
                              {isOpen && (
                                <div className="px-3 pb-3 border-t border-slate-100 pt-2 space-y-2 text-xs">
                                  {doc.original_text && <div><strong className="text-muted-foreground">Original:</strong> <span>{doc.original_text}</span></div>}
                                  {doc.reframed_text && <div><strong className="text-muted-foreground">AI Reframed:</strong> <span className="text-violet-700">{doc.reframed_text}</span></div>}
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </CardContent>
                  </Card>

                  {/* Service Entries */}
                  <Card className="border-0 shadow-sm rounded-2xl" data-testid="heat-individual-services">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        <CalendarDays size={15} className="text-amber-600" /> Service Entries ({clientDocs.service_entries?.length || 0})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {(clientDocs.service_entries || []).length === 0 ? (
                        <p className="text-sm text-muted-foreground">No service entries for this client.</p>
                      ) : (
                        clientDocs.service_entries.slice(0, 10).map(svc => (
                          <div key={svc.id} className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2 text-xs" data-testid={`service-entry-${svc.id}`}>
                            <div>
                              <p className="font-medium">{svc.service_type}</p>
                              <p className="text-muted-foreground">{svc.date} - {svc.duration_minutes || 0} min</p>
                            </div>
                            <Badge variant="outline" className="text-[10px]">{svc.verification_status}</Badge>
                          </div>
                        ))
                      )}
                    </CardContent>
                  </Card>

                  {/* Peer Support Linkage */}
                  {clientDocs.client?.in_peer_support && (
                    <Card className="border-0 shadow-sm rounded-2xl border-teal-100 bg-teal-50/30" data-testid="heat-peer-support-link">
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-teal-100 flex items-center justify-center">
                            <ExternalLink className="w-4 h-4 text-teal-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm text-teal-800">Enrolled in Peer Support Program</p>
                            <p className="text-xs text-teal-600">This client has documentation in the Peer Support system as well.</p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full border-teal-300 text-teal-700 hover:bg-teal-100"
                          onClick={() => navigate('/dashboard')}
                          data-testid="heat-go-peer-support"
                        >
                          View in Peer Support
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </div>
              ) : null}
            </>
          )}
        </div>
      )}

      {/* ============ DIALOGS ============ */}
      <Dialog open={supportOpen} onOpenChange={setSupportOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Request Coordinator Support</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <DictationInput
              value={supportMessage}
              onChange={(cleaned) => setSupportMessage(cleaned)}
              rows={4}
              placeholder="Share what you need help with..."
              promptContext={{
                context: 'heat_support',
                taskName: 'Request Coordinator Support',
                taskDescription: 'Program escalation request',
                category: 'support'
              }}
              data-testid="heat-support-input"
            />
            <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full" onClick={handleSupportRequest} data-testid="heat-support-submit">
              Send Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Group Note Message Dialog */}
      <Dialog open={!!groupMessageOpen} onOpenChange={(open) => { if (!open) setGroupMessageOpen(null); }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send size={16} className="text-orange-600" /> Message Coordinator & ED
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              This message will be sent to the Program Coordinator and Executive Director regarding the selected group note.
            </p>
            <DictationInput
              value={groupMessageText}
              onChange={(cleaned) => setGroupMessageText(cleaned)}
              rows={4}
              placeholder="Type your message about this group session..."
              promptContext={{
                context: 'heat_group_message',
                taskName: 'Group Note Communication',
                taskDescription: 'Message to coordinator/ED about group documentation',
                category: 'communication'
              }}
              data-testid="group-message-input"
            />
            <Button
              className="bg-orange-600 hover:bg-orange-700 text-white rounded-full gap-2"
              onClick={() => handleSendGroupMessage(groupMessageOpen)}
              data-testid="group-message-send"
            >
              <Send size={14} /> Send Message
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
